import { Injectable, signal } from '@angular/core';

/**
 * Single source of truth for "are we running inside the embeddable chatbot widget?".
 *
 * The widget is served from the SAME ciceroneweb build, on the `/chatbot-embed`
 * route (loaded in an <iframe> on a client's site). Several places need to behave
 * differently in embed mode — the app bootstrap, the i18n loader, route submission,
 * and the planner (which hides its "send route" action because that flow needs the
 * CMS app-token, a backend gap). Instead of scattering `pathname.includes(...)`
 * checks, they all read this flag.
 *
 * It is derived ONCE from the pathname at construction (the embed route never changes
 * mid-session), so it is safe to read synchronously from anywhere, including very
 * early in bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class EmbedContextService {
  /** True when the current document is the embeddable chatbot widget. */
  public readonly isEmbed = signal<boolean>(
    typeof window !== 'undefined' && !!window.location?.pathname?.includes('chatbot-embed')
  );

  /**
   * The widget's chatbotId, resolved either from `?id=` in the iframe URL or from the
   * parent's `postMessage` handshake (the real CMS embed script — `cicerone-chat.js` —
   * sends it via postMessage and loads the iframe with NO query params).
   *
   * Bootstrap (app.component) WAITS for this before resolving the tenant + device, so it
   * never calls `getCommonApp` with the hostname-derived subdomain (which 404s and lands
   * on not-found). It is a shared signal because the value is produced by the routed
   * `ChatbotEmbedComponent` (postMessage listener) but consumed by the root app.component.
   */
  public readonly chatbotId = signal<string | null>(null);

  public showMaximizeMinimizeButton = signal<boolean>(false);
}
